import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslate } from "react-admin";

import {
  CustomerServiceBillingCustomerDocument,
  CustomerServiceBillingCustomerQuery,
  CustomerServiceBillingCustomerQueryVariables,
  CustomerServiceBillingSalesforceAccountsDocument,
  CustomerServiceBillingSalesforceAccountsQuery,
  CustomerServiceBillingSalesforceAccountsQueryVariables,
  CustomerServiceOrganizationBillingAccessQuery,
  SetOrganizationBillingAccountDocument,
  SetOrganizationBillingAccountMutation,
  SetOrganizationBillingAccountMutationVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";
import { captureException } from "../lib/errorHandling";

export type BillingAccessDialogValue =
  CustomerServiceOrganizationBillingAccessQuery["customerServiceOrganizationBillingAccess"][number];
type SalesforceAccount =
  CustomerServiceBillingSalesforceAccountsQuery["customerServiceBillingSalesforceAccounts"][number];
type BillingCustomer = NonNullable<CustomerServiceBillingCustomerQuery["customerServiceBillingCustomer"]>;
type Props = {
  access: BillingAccessDialogValue;
  organizationName: string;
  onClose: () => void;
  onReload: () => void;
  onSaved: (access: BillingAccessDialogValue) => void;
};

export const OrganizationBillingAccountDialog = ({ access, organizationName, onClose, onReload, onSaved }: Props) => {
  const translate = useTranslate();
  const [account, setAccount] = useState<SalesforceAccount | null>(
    access.salesforceAccountId
      ? { id: access.salesforceAccountId, name: access.salesforceAccountName || access.salesforceAccountId }
      : null
  );
  const [accounts, setAccounts] = useState<SalesforceAccount[]>([]);
  const [customerId, setCustomerId] = useState(access.frisbiiCustomerId || "");
  const [customer, setCustomer] = useState<BillingCustomer | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [lookupError, setLookupError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const searchRequestRef = useRef(0);
  const customerRequestRef = useRef(0);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const invalidate = () => {
    customerRequestRef.current += 1;
    setCustomer(null);
    setConfirmed(false);
    setChecking(false);
    setLookupError(false);
    setLookupAttempted(false);
  };
  const search = async (value: string) => {
    const request = ++searchRequestRef.current;
    setSearchError(false);
    try {
      const result = await client.query<
        CustomerServiceBillingSalesforceAccountsQuery,
        CustomerServiceBillingSalesforceAccountsQueryVariables
      >({
        query: CustomerServiceBillingSalesforceAccountsDocument,
        variables: { search: value },
        fetchPolicy: "no-cache",
      });
      if (mountedRef.current && request === searchRequestRef.current)
        setAccounts(result.data.customerServiceBillingSalesforceAccounts);
    } catch (error) {
      captureException(error, "billing/account: search Salesforce accounts");
      if (mountedRef.current && request === searchRequestRef.current) setSearchError(true);
    }
  };
  const check = async () => {
    if (!account || saving) return;
    const request = ++customerRequestRef.current;
    setChecking(true);
    setLookupError(false);
    setLookupAttempted(true);
    setCustomer(null);
    setConfirmed(false);
    try {
      const result = await client.query<
        CustomerServiceBillingCustomerQuery,
        CustomerServiceBillingCustomerQueryVariables
      >({
        query: CustomerServiceBillingCustomerDocument,
        variables: { salesforceAccountId: account.id, frisbiiCustomerId: customerId.trim() || null },
        fetchPolicy: "no-cache",
      });
      if (mountedRef.current && request === customerRequestRef.current)
        setCustomer(result.data.customerServiceBillingCustomer ?? null);
    } catch (error) {
      captureException(error, "billing/account: check Frisbii customer");
      if (mountedRef.current && request === customerRequestRef.current) setLookupError(true);
    } finally {
      if (mountedRef.current && request === customerRequestRef.current) setChecking(false);
    }
  };
  const save = async (detach: boolean) => {
    if (savingRef.current || (!detach && (!account || !customer || !confirmed))) return;
    const targetSalesforceAccountId = detach ? access.salesforceAccountId : account?.id;
    if (!targetSalesforceAccountId) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      const result = await client.mutate<
        SetOrganizationBillingAccountMutation,
        SetOrganizationBillingAccountMutationVariables
      >({
        mutation: SetOrganizationBillingAccountDocument,
        variables: {
          organizationId: access.organizationId,
          salesforceAccountId: targetSalesforceAccountId,
          frisbiiCustomerId: detach ? null : customer?.id,
          expectedMappingVersion: access.mappingVersion,
          confirmed: true,
        },
      });
      const updated = result.data?.setOrganizationBillingAccount;
      if (
        !updated ||
        updated.organizationId !== access.organizationId ||
        updated.salesforceAccountId !== targetSalesforceAccountId ||
        updated.frisbiiCustomerId !== (detach ? null : customer?.id) ||
        updated.accessEnabled ||
        !updated.mappingVersion
      )
        throw new Error("Conflicting billing account response");
      if (!mountedRef.current) return;
      onSaved(updated);
      onClose();
    } catch (error) {
      captureException(error, "billing/account: save billing account");
      if (mountedRef.current) setSaveError(true);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate("billingAccess.linkTitle", { organization: organizationName })}</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={accounts}
          value={account}
          getOptionLabel={option => `${option.name} (${option.id})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={options => options}
          onOpen={() => void search("")}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") void search(value);
          }}
          onChange={(_, value) => {
            setAccount(value);
            invalidate();
          }}
          disabled={saving}
          renderInput={params => <TextField {...params} label={translate("billingAccess.salesforceAccount")} />}
        />
        {searchError && <Typography color="error">{translate("billingAccess.searchError")}</Typography>}
        <TextField
          fullWidth
          value={customerId}
          disabled={saving}
          label={translate("billingAccess.frisbiiCustomerId")}
          onChange={event => {
            setCustomerId(event.target.value);
            invalidate();
          }}
        />
        <Button disabled={!account || checking || saving} onClick={() => void check()}>
          {translate("billingAccess.checkCustomer")}
        </Button>
        {customer && (
          <Typography>
            {[customer.displayName, customer.id, customer.externalCustomerId, customer.debtorAccount]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}
        {lookupAttempted && !checking && !lookupError && !customer && (
          <Typography>{translate("billingAccess.customerNotFound")}</Typography>
        )}
        {lookupError && <Typography color="error">{translate("billingAccess.customerError")}</Typography>}
        {saveError && (
          <>
            <Typography color="error">{translate("billingAccess.linkSaveError")}</Typography>
            <Button disabled={saving} onClick={onReload}>
              {translate("billingAccess.reloadMapping")}
            </Button>
          </>
        )}
        <Typography>{translate("billingAccess.accessOffAfterSave")}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={event => setConfirmed(event.target.checked)}
              disabled={saving || !customer}
            />
          }
          label={translate("billingAccess.confirmEntitlement")}
        />
        {access.frisbiiCustomerId && (
          <Button disabled={saving} onClick={() => void save(true)}>
            {translate("billingAccess.detach")}
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose}>
          {translate("ra.action.cancel")}
        </Button>
        <Button disabled={saving || !customer || !confirmed || !account} onClick={() => void save(false)}>
          {translate("ra.action.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
